import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../../services/firebase";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeEmailList(values = []) {
  const list = Array.isArray(values)
    ? values
    : String(values || "").split(/[;,\n]+/);
  const seen = new Set();

  return list
    .map((value) => String(value || "").trim().toLowerCase())
    .filter((email) => {
      if (!email || !EMAIL_RE.test(email) || seen.has(email)) return false;
      seen.add(email);
      return true;
    });
}

function getManagerEmails(record = {}) {
  return normalizeEmailList([
    ...(Array.isArray(record.managerEmails) ? record.managerEmails : []),
    record.managerEmail || "",
  ]);
}

function driverName(driver = {}) {
  return driver.name || driver.displayName || driver.email || driver.id || "Driver";
}

function normalizeCompanyIds(driver = {}) {
  const ids = Array.isArray(driver.companyIds) ? driver.companyIds : [];
  if (driver.companyId && !ids.includes(driver.companyId)) return [...ids, driver.companyId];
  return ids;
}

export default function DriverCompaniesTab({ companies = [], selectedCompanyId = "" }) {
  const [drivers, setDrivers] = useState([]);
  const [managerDirectory, setManagerDirectory] = useState([]);
  const [filter, setFilter] = useState("");
  const [savingId, setSavingId] = useState("");
  const [openDriverIds, setOpenDriverIds] = useState(() => new Set());

  const toggleDriverOpen = (driverId) => {
    setOpenDriverIds((prev) => {
      const next = new Set(prev);
      if (next.has(driverId)) next.delete(driverId);
      else next.add(driverId);
      return next;
    });
  };

  useEffect(() => {
    const qy = query(collection(db, "users"), where("role", "==", "driver"));
    return onSnapshot(qy, (snap) => {
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => driverName(a).localeCompare(driverName(b)));
      setDrivers(list);
    });
  }, []);

  useEffect(() => {
    return onSnapshot(collection(db, "notificationManagers"), (snap) => {
      const list = snap.docs
        .map((d) => normalizeEmailList(d.data()?.email || d.id)[0])
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      setManagerDirectory(list);
    });
  }, []);

  const companyById = useMemo(() => {
    const map = new Map();
    companies.forEach((company) => map.set(company.id, company));
    return map;
  }, [companies]);

  const managerOptions = useMemo(() => {
    const emails = [
      ...managerDirectory,
      ...companies.flatMap(getManagerEmails),
      ...drivers.flatMap(getManagerEmails),
    ];
    return normalizeEmailList(emails).sort((a, b) => a.localeCompare(b));
  }, [companies, drivers, managerDirectory]);

  const getInheritedManagerEmails = useCallback(
    (driver) =>
      normalizeEmailList(
        normalizeCompanyIds(driver).flatMap((id) =>
          getManagerEmails(companyById.get(id) || {})
        )
      ),
    [companyById],
  );

  const filteredDrivers = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return drivers;
    return drivers.filter((driver) => {
      const companyNames = normalizeCompanyIds(driver)
        .map((id) => companyById.get(id)?.name || id)
        .join(" ");
      const managerText = [
        ...getManagerEmails(driver),
        ...getInheritedManagerEmails(driver),
      ].join(" ");
      return `${driverName(driver)} ${driver.email || ""} ${companyNames} ${managerText}`
        .toLowerCase()
        .includes(q);
    });
  }, [companyById, drivers, filter, getInheritedManagerEmails]);

  async function toggleCompany(driver, companyId) {
    const current = normalizeCompanyIds(driver);
    const next = current.includes(companyId)
      ? current.filter((id) => id !== companyId)
      : [...current, companyId];

    setSavingId(driver.id);
    try {
      await updateDoc(doc(db, "users", driver.id), {
        companyIds: next,
        companyId: next[0] || "",
        updatedAt: serverTimestamp(),
      });
    } finally {
      setSavingId("");
    }
  }

  async function toggleManager(driver, managerEmail) {
    const current = getManagerEmails(driver);
    const next = current.includes(managerEmail)
      ? current.filter((email) => email !== managerEmail)
      : [...current, managerEmail];

    setSavingId(driver.id);
    try {
      await updateDoc(doc(db, "users", driver.id), {
        managerEmails: normalizeEmailList(next),
        updatedAt: serverTimestamp(),
      });
    } finally {
      setSavingId("");
    }
  }

  return (
    <div className="space-y-4 text-gray-900">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400 sm:w-80"
          placeholder="Search drivers or companies..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="text-sm text-gray-500">
          {drivers.length} driver{drivers.length === 1 ? "" : "s"} ·{" "}
          {managerOptions.length} manager{managerOptions.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {filteredDrivers.length === 0 ? (
          <div className="px-4 py-8 text-sm text-gray-500">No drivers found.</div>
        ) : (
          filteredDrivers.map((driver) => {
            const assignedIds = normalizeCompanyIds(driver);
            const directManagers = getManagerEmails(driver);
            const inheritedManagers = getInheritedManagerEmails(driver);
            const isSaving = savingId === driver.id;
            const isOpen = openDriverIds.has(driver.id);

            return (
              <div
                key={driver.id}
                className="border-t border-gray-100 p-4 first:border-t-0"
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => toggleDriverOpen(driver.id)}
                      aria-expanded={isOpen}
                      className="flex items-center gap-2 text-left"
                    >
                      <div className="font-semibold text-gray-900">{driverName(driver)}</div>
                      <svg
                        className={`h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                    <div className="mt-1 break-all text-sm text-gray-500">
                      {driver.email || driver.id}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {assignedIds.length ? (
                        assignedIds.map((id) => (
                          <span
                            key={id}
                            className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800"
                          >
                            {companyById.get(id)?.name || id}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-500">
                          No companies assigned
                        </span>
                      )}
                    </div>

                    <div className="mt-3 space-y-2">
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
                          Managers
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {directManagers.length ? (
                            directManagers.map((email) => (
                              <span
                                key={`direct-${email}`}
                                className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-800"
                              >
                                Direct: {email}
                              </span>
                            ))
                          ) : null}
                          {inheritedManagers.length ? (
                            inheritedManagers.map((email) => (
                              <span
                                key={`inherited-${email}`}
                                className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700"
                              >
                                Company: {email}
                              </span>
                            ))
                          ) : null}
                          {!directManagers.length && !inheritedManagers.length ? (
                            <span className="text-xs text-gray-500">
                              No managers assigned
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>

                  {isOpen && (
                  <div className="space-y-4">
                    <div>
                      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
                        Companies
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                        {companies.map((company) => {
                          const checked = assignedIds.includes(company.id);
                          return (
                            <label
                              key={company.id}
                              className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${
                                checked
                                  ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                              } ${
                                selectedCompanyId === company.id ? "ring-2 ring-emerald-100" : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={isSaving}
                                onChange={() => toggleCompany(driver, company.id)}
                                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                              />
                              <span className="min-w-0 truncate">{company.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
                        Direct managers
                      </div>
                      {managerOptions.length ? (
                        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                          {managerOptions.map((email) => {
                            const checked = directManagers.includes(email);
                            return (
                              <label
                                key={email}
                                className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${
                                  checked
                                    ? "border-sky-300 bg-sky-50 text-sky-900"
                                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={isSaving}
                                  onChange={() => toggleManager(driver, email)}
                                  className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                                />
                                <span className="min-w-0 truncate">{email}</span>
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-gray-200 px-3 py-2 text-sm text-gray-500">
                          Add manager emails on the Managers page or company settings.
                        </div>
                      )}
                    </div>
                  </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
